// @ts-check

import React, { useMemo, useRef, useState } from 'react';
import { CropFree, ZoomOutMap } from '@material-ui/icons';
import { AppBar, IconButton, Toolbar, Tooltip, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useFullscreen } from 'react-use';
import HeaderNotifications from '../../components/HeaderNotifications';
import Profile from './Profile';
import SidebarToggleHandler from '../../@coremat/CmtLayouts/Vertical/SidebarToggleHandler';
import DashboardSearch from './DashboardSearch';

/**
 * @typedef HeaderProps
 * @property {string | undefined} id
 * @property {string | undefined} className
 */

/**
 * Header
 *
 * @param {HeaderProps} props
 * @return {React.JSX.Element}
 */
export default function Header(props) {
  const { id, className } = props;
  const location = useLocation();
  const documentRef = useRef(/** @type {HTMLElement} */ (document.documentElement));
  const [isFullscreen, setIsFullscreen] = useState(/** @type {boolean} */ (false));

  useFullscreen(documentRef, isFullscreen, {
    onClose: () => setIsFullscreen(false),
  });

  /**
   * @type {string}
   */
  const title = useMemo(() => {
    switch (location.pathname) {
    case '/account-summary/':
    case '/account-summary':
    case '/patient-search/':
    case '/patient-search':
      return 'Account Summary';
    case '/letters/letter-maintenance':
    case '/letters/letter-maintenance/':
      return 'Letter Maintenance';
    case '/letters/letter-request':
    case '/letters/letter-request/':
      return 'Letter Request';
    case '/letters/letter-batches':
    case 'letters/letter-batches/':
      return 'Letter Batches';
    case '/client-organization':
    case '/client-organization/':
      return 'Client Maintenance';
    case '/client-tenants':
    case '/client-tenants/':
      return 'Tenants';
    case '/billing-indicator-maintenance':
    case '/billing-indicator-maintenance/':
      return 'Billing Indicator Maintenance';
    case '/frameworks/data-repository':
    case '/frameworks/data-repository/':
      return 'Data Repository';
    case '/frameworks/data-repository/repository-fields':
    case '/frameworks/data-repository/repository-fields/':
      return 'Repository Fields';
    case '/frameworks/staging-repository':
    case '/frameworks/staging-repository/':
      return 'Staging Repository';
    case '/frameworks/audit-field-list':
    case '/frameworks/audit-field-list/':
      return 'Audit Field List';
    case '/frameworks/client-audit-field-list':
    case '/frameworks/client-audit-field-list/':
      return 'Client Audit Field List';
    case '/frameworks/reports':
    case '/frameworks/reports/':
      return 'Reporting/Exports';
    case '/frameworks/import-template-record':
    case '/frameworks/import-template-record/':
      return 'Import Template Records';
    case '/frameworks/import-record-group':
    case '/frameworks/import-record-group/':
      return 'Import Record Groups';
    case '/frameworks/import-manager':
    case '/frameworks/import-manager/':
      return 'Import Manager';
    case '/frameworks/dm-import-execution-history':
    case '/frameworks/dm-import-execution-history/':
      return 'Execution History';
    case '/frameworks/export-template-record':
    case '/frameworks/export-template-record/':
      return 'Export Template Records';
    case '/frameworks/export-record-group':
    case '/frameworks/export-record-group/':
      return 'Export Record Groups';
    case '/frameworks/export-manager':
    case '/frameworks/export-manager/':
      return 'Export Manager';
    case '/frameworks/dm-export-execution-history':
    case '/frameworks/dm-export-execution-history/':
      return 'Execution History';
    case '/frameworks/data-calculation':
    case '/frameworks/data-calculation/':
      return 'Data Calculations';
    case '/frameworks/filter-rules':
    case '/frameworks/filter-rules/':
      return 'Filter Rules';
    case '/frameworks/event-tactics/triggers':
    case '/frameworks/event-tactics/triggers/':
      return 'Trigger Maintenance';
    case '/frameworks/event-tactics/trigger-group':
    case '/frameworks/event-tactics/trigger-group/':
      return 'Batch Triggers';
    case '/frameworks/event-tactics/event-trigger-group':
    case '/frameworks/event-tactics/event-trigger-group/':
      return 'Event Triggers';
    case '/frameworks/event-tactics/account-update-group':
    case '/frameworks/event-tactics/account-update-group/':
      return 'Account Update';
    case '/work-queue':
    case '/work-queue/':
      return 'Work Queue - Assigned';
    case '/work-queue/owned':
    case '/work-queue/owned/':
      return 'Work Queue - Owned';
    case '/work-queue-setup':
    case '/work-queue-setup/':
      return 'Work Queue Setup';
    case '/work-queue-activity':
    case '/work-queue-activity/':
      return 'Work Queue Activity';
    case '/new-business':
    case '/new-business/':
      return 'New Business';
    case '/new-business/approved':
    case '/new-business/approved/':
      return 'New Business - Approved';
    case '/new-business/rejected':
    case '/new-business/rejected/':
      return 'New Business - Rejected';
    case '/new-business-maintenance/add-account':
    case '/new-business-maintenance/add-account/':
      return 'Add Account';
    case '/frameworks/scheduler':
    case '/frameworks/scheduler/':
      return 'Scheduler';
    case '/scheduler-execution-history':
    case '/scheduler-execution-history/':
      return 'Scheduler Execution History';
    case '/lookup-maintenance':
    case '/lookup-maintenance/':
      return 'Lookup Maintenance';
    case '/user-management':
    case '/user-management/':
      return 'User Management';
    case '/role-management':
    case '/role-management/':
      return 'Role Management';
    case '/custom-window':
    case '/custom-window/':
      return 'Custom Window';
    case '/translation-table':
    case '/translation-table/':
      return 'Translation Table';
    default:
      return 'Dashboard';
    }
  }, [location]);

  return (
    <AppBar
      id={id}
      className={className}
      position="sticky"
      style={{ top: `0px`, backgroundColor: '#2C1570', color: '#ffffff' }}
    >
      <Toolbar>
        <SidebarToggleHandler />
        <Typography variant="h6" component="h1" color="inherit" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Tooltip title="Fullscreen">
          <IconButton
            color="inherit"
            onClick={() => setIsFullscreen((prevIsFullScreen) => !prevIsFullScreen)}
            aria-label="Fullscreen"
          >
            {isFullscreen ? <CropFree /> : <ZoomOutMap />}
          </IconButton>
        </Tooltip>
        <HeaderNotifications />
        <Profile />
      </Toolbar>
      <Toolbar variant="dense" sx={{ backgroundColor: '#ffffff', color: '#000000' }}>
        <DashboardSearch />
      </Toolbar>
    </AppBar>
  );
}
